import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Button, Section, useToggle } from '@factorialco/deprecated-design-system'
import blankSlate from 'assets/images/list_blankslate.svg'
import {
  type PermissionsGroupEmployeesPageItemFragment,
  PermissionsGroupEmployeesPageQueryDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { PermissionGroupsLayout } from 'modules/permissions/components/PermissionGroupsLayout'
import AssignUsersModal from 'modules/permissions/components/Permissions/AssignUsersModal'
import {
  PERMISSION_GROUP_KIND_ADMIN,
  PERMISSION_GROUP_KIND_DEFAULT,
} from 'modules/permissions/lib/constants'
import { PermissionGroupContainerProps } from 'modules/permissions/pages/settings/PermissionGroupContainer'

import Item from './Item'

const Employees = observer(() => {
  const { permissionGroup } = useOutletContext<PermissionGroupContainerProps>()

  const { data, loading, refetch } = useQuery(PermissionsGroupEmployeesPageQueryDocument, {
    variables: {
      permissionGroupId: permissionGroup.id,
    },
  })

  const members: PermissionsGroupEmployeesPageItemFragment[] =
    data?.permissions.permissionGroup?.members ?? []

  const [isEmployeesModalOpen, setIsEmployeesModalOpen] = React.useState(false)
  const [open, toggle] = useToggle(false)

  const description = (): string => {
    if (permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_ADMIN) {
      return i18n.t('settings.permissions.group.employees_section.legacy_admin.description')
    }

    if (permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_DEFAULT) {
      return i18n.t('settings.permissions.group.employees_section.default_group.description')
    }

    return i18n.t('settings.permissions.group.employees_section.other_group.description')
  }

  const options = (): Option[] => {
    const allowedOptions = []

    if (data?.permissions._policies.groupAssignEmployees.can ?? false) {
      allowedOptions.push({
        onClick: () => setIsEmployeesModalOpen(true),
        title: i18n.t('settings.permissions.group.actions.assign_employees.title'),
        description: i18n.t('settings.permissions.group.actions.assign_employees.description'),
      })
    }

    return allowedOptions
  }

  const renderList = () => {
    if (members.length === 0) {
      return <img src={blankSlate} height={300} />
    }

    return (
      <BoxList>
        {members.map((member) => (
          <Item
            key={member.id}
            user={member}
            canAssign={data?.permissions._policies.groupAssignEmployees.can ?? false}
            onUpdate={refetch}
          />
        ))}
      </BoxList>
    )
  }

  return (
    <ResponsivePage.Body showHeader={false} loading={loading} error={null}>
      <PermissionGroupsLayout />
      <Section.FullWidth
        illustration="group"
        title={permissionGroup.title}
        description={description()}
      >
        {options().length && (
          <Box alignItems="flexEnd" paddingBottom="s24">
            <Popover
              attachment="top right"
              targetAttachment="bottom center"
              toggle={toggle}
              open={open}
            >
              <Button onClick={toggle(true)} iconName="add" />
              <PopoverContent onClose={toggle(false)} options={options()} />
            </Popover>
          </Box>
        )}
        {isEmployeesModalOpen && (
          <AssignUsersModal
            onClose={() => {
              setIsEmployeesModalOpen(false)
              refetch()
            }}
            permissionGroup={permissionGroup}
            excludeIds={members.map((member) => member.id)}
            external={false}
          />
        )}
        <>
          {renderList()}
          <Outlet />
        </>
      </Section.FullWidth>
    </ResponsivePage.Body>
  )
})

export default Employees
